/* TFN header: hide-on-scroll, editorial dropdown, mobile overlay. */
class TfnHeader extends HTMLElement {
  constructor() {
    super();
    this.bar = this.querySelector('.tfn-header__bar');
    this.dropdown = this.querySelector('#tfn-dropdown');
    this.shopTrigger = this.querySelector('.tfn-header__shop');
    this.hamburger = this.querySelector('.tfn-header__hamburger');
    this.overlay = document.getElementById('tfn-mobile-overlay');
    this.overlayClose = this.overlay ? this.overlay.querySelector('.tfn-mobile-overlay__close') : null;
    this.closeTimer = null;
    this.lastScroll = 0;
    this.headerHeight = 0;
  }

  connectedCallback() {
    this.setHeight();
    window.addEventListener('resize', this.setHeight.bind(this));

    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.lastScroll = window.pageYOffset || document.documentElement.scrollTop;
    // Initial state: at the top of the page (black on homepage via CSS scope).
    this.classList.toggle('tfn-header--at-top', this.lastScroll <= this.headerHeight);

    // Once the hide-slide finishes, drop the at-top dark state while the header
    // is off-screen, so it reveals mid-page in the white state with no flash.
    this.addEventListener('transitionend', (event) => {
      if (
        event.target === this &&
        event.propertyName === 'transform' &&
        this.classList.contains('tfn-header--hidden')
      ) {
        this.classList.remove('tfn-header--at-top');
      }
    });

    this.bindDropdown();
    this.bindMobileOverlay();

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeDropdown();
        this.closeMobile();
      }
    });
  }

  setHeight() {
    if (!this.bar) return;
    this.headerHeight = this.bar.offsetHeight;
    document.documentElement.style.setProperty('--tfn-header-h', `${this.headerHeight / 10}rem`);
  }

  /* --- Hide on scroll down, reveal on any scroll up --- */
  onScroll() {
    const current = window.pageYOffset || document.documentElement.scrollTop;

    if (current <= this.headerHeight) {
      // Always visible near the top; at-top colour state (black on homepage).
      this.classList.remove('tfn-header--hidden');
      this.classList.add('tfn-header--at-top');
    } else if (current > this.lastScroll) {
      // Scrolling down -> hide. Keep tfn-header--at-top here so the header stays
      // in its dark state for the whole slide-away (no white flash); it's dropped
      // off-screen once the slide finishes (see transitionend in connectedCallback).
      this.classList.add('tfn-header--hidden');
      this.closeDropdown();
    } else if (current < this.lastScroll) {
      // Scrolling up.
      this.classList.remove('tfn-header--hidden');
      this.classList.remove('tfn-header--at-top');
    }

    this.lastScroll = current;
  }

  /* --- Editorial dropdown --- */
  bindDropdown() {
    if (!this.dropdown || !this.shopTrigger) return;

    const open = () => this.openDropdown();
    const scheduleClose = () => {
      this.closeTimer = window.setTimeout(() => this.closeDropdown(), 150);
    };
    const cancelClose = () => window.clearTimeout(this.closeTimer);

    this.shopTrigger.addEventListener('mouseenter', () => {
      cancelClose();
      open();
    });
    this.shopTrigger.addEventListener('mouseleave', scheduleClose);
    this.shopTrigger.addEventListener('focus', open);

    this.dropdown.addEventListener('mouseenter', cancelClose);
    this.dropdown.addEventListener('mouseleave', scheduleClose);

    // Shop is now a real <a> to the catalog: a click navigates. No click-to-toggle handler —
    // the dropdown still opens on hover (above) and on keyboard focus (below). Touch/keyboard
    // users can still reach the dropdown's links via focus; activating "Shop" goes to the catalog.
  }

  openDropdown() {
    this.dropdown.classList.add('is-open');
    this.dropdown.setAttribute('aria-hidden', 'false');
    this.shopTrigger.setAttribute('aria-expanded', 'true');
  }

  closeDropdown() {
    if (!this.dropdown) return;
    this.dropdown.classList.remove('is-open');
    this.dropdown.setAttribute('aria-hidden', 'true');
    if (this.shopTrigger) this.shopTrigger.setAttribute('aria-expanded', 'false');
  }

  /* --- Mobile overlay --- */
  bindMobileOverlay() {
    if (!this.hamburger || !this.overlay) return;

    this.hamburger.addEventListener('click', () => this.openMobile());
    if (this.overlayClose) {
      this.overlayClose.addEventListener('click', () => this.closeMobile());
    }
  }

  openMobile() {
    this.overlay.classList.add('is-open');
    this.overlay.setAttribute('aria-hidden', 'false');
    this.hamburger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  closeMobile() {
    if (!this.overlay) return;
    this.overlay.classList.remove('is-open');
    this.overlay.setAttribute('aria-hidden', 'true');
    if (this.hamburger) this.hamburger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }
}

customElements.define('tfn-header', TfnHeader);
